import React, { useState, useRef, useEffect } from "react";
import { URL } from "../config";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

const BR = "#1D4ED8", DK = "#072877";

const today   = () => new Date().toISOString().split("T")[0];
const nowTime = () => { const d = new Date(); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; };

const inp = {
  width:"100%", height:42, border:"1.5px solid #CBD5E1", borderRadius:8,
  padding:"0 12px", fontSize:14, fontFamily:"'Poppins', sans-serif", color:"#1E293B",
  outline:"none", boxSizing:"border-box", background:"#fff",
  transition:"border-color .15s",
};
const lbl = {
  display:"block", fontSize:12.5, fontWeight:600, color:BR,
  marginBottom:6, letterSpacing:"0.2px", fontFamily:"'Poppins', sans-serif",
};
const g        = (c, gap=16) => ({ display:"grid", gridTemplateColumns:`repeat(${c},1fr)`, gap });
const divider  = { height:1, background:"#E2E8F0", margin:"18px 0" };
const custBar  = { background:"#EFF6FF", borderLeft:`4px solid ${BR}`, borderRadius:"0 8px 8px 0", padding:"12px 16px", display:"flex", flexWrap:"wrap", gap:"8px 22px", fontSize:13, color:"#475569", marginBottom:18, fontFamily:"'Poppins', sans-serif" };
const cardBody = { padding:"16px 20px 20px", overflow:"visible" };
const card     = { background:"#fff", borderRadius:0, border:"none", borderBottom:"1px solid #E2E8F0", boxShadow:"0 2px 12px rgba(7,40,119,.06)" };

if (typeof document !== "undefined" && !document.getElementById("poppins-font")) {
  const link = document.createElement("link");
  link.id   = "poppins-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

const AVATAR_COLORS = [
  "#1D4ED8","#7C3AED","#DB2777","#D97706","#059669",
  "#DC2626","#2563EB","#9333EA","#EA580C","#0891B2",
];

const getAvatarColor = name => {
  const safeName = String(name || "");
  if (!safeName) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < safeName.length; i++) hash = safeName.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = name => {
  const safeName = String(name || "").trim();
  if (!safeName) return "?";
  const parts = safeName.split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getCommIcon = name => {
  if (!name) return "💬";
  const n = name.toLowerCase();
  if (n.includes("whatsapp"))                    return "🟢";
  if (n.includes("call") || n.includes("phone")) return "📞";
  if (n.includes("facebook"))                    return "🔵";
  if (n.includes("visit"))                       return "🚶";
  if (n.includes("meeting"))                     return "👥";
  if (n.includes("email"))                       return "✉️";
  if (n.includes("sms"))                         return "💬";
  return "💬";
};

// Priority options — matches backend enum: 1=High, 2=Medium, 3=Low
const PRIORITY_OPTIONS = [
  { id: 1, name: "🔴 High"   },
  { id: 2, name: "🟡 Medium" },
  { id: 3, name: "🟢 Low"    },
];

// ─── Get logged-in employee ID from localStorage ──────────────────────────
const getLoggedInUserId = () => {
  try {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.userid || null;
    }
  } catch { }
  return null;
};

// ─── Avatar ───────────────────────────────────────────────────────────────
function Avatar({ name, size = 26 }) {
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", justifyContent:"center",
      width:size, height:size, borderRadius:"50%",
      background:getAvatarColor(name), color:"#fff",
      fontSize:size * 0.42, fontWeight:700, flexShrink:0, lineHeight:1,
      fontFamily:"'Poppins', sans-serif",
    }}>
      {getInitials(name)}
    </span>
  );
}

// ─── CustomerSD ───────────────────────────────────────────────────────────
function CustomerSD({ options, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef();
  const inputRef        = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selected   = options.find(o => String(o.id || o) === String(value));
  const selName    = selected ? (selected.name || selected) : "";
  const displayVal = open ? q : selName;
  const filtered   = q.trim()
    ? options.filter(o => {
        const sq = q.toLowerCase();
        return (
          (o.name        || "").toLowerCase().includes(sq) ||
          (o.mobile      || "").toLowerCase().includes(sq) ||
          (o.email       || "").toLowerCase().includes(sq) ||
          (o.city        || "").toLowerCase().includes(sq) ||
          (o.designation || "").toLowerCase().includes(sq)
        );
      })
    : options.slice(0, 50);

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div style={{ position:"relative" }}>
        {selName && !open && (
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", zIndex:1, pointerEvents:"none" }}>
            <Avatar name={selName} size={24} />
          </span>
        )}
        <input
          ref={inputRef}
          style={{
            ...inp,
            paddingLeft: selName && !open ? 42 : 12,
            paddingRight: 32,
            cursor: "text",
            borderColor: open ? BR : "#CBD5E1",
            ...(open ? { boxShadow:"0 0 0 3px rgba(29,78,216,.10)" } : {}),
          }}
          value={displayVal}
          placeholder={loading ? "Loading customers..." : ""}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
        />
        <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:9, color:"#94A3B8", pointerEvents:"none" }}>
          {open ? "▲" : "▼"}
        </span>
      </div>
      {open && (
        <div style={{
          position:"absolute", top:46, left:0, right:0, background:"#fff",
          border:"1.5px solid #CBD5E1", borderRadius:10, zIndex:9999,
          boxShadow:"0 8px 24px rgba(0,0,0,.12)", maxHeight:240, overflowY:"auto",
        }}>
          {filtered.length === 0 && (
            <div style={{ padding:"12px 14px", color:"#94A3B8", fontSize:13, textAlign:"center", fontFamily:"'Poppins', sans-serif" }}>
              No customers found
            </div>
          )}
          {filtered.map((o, i) => {
            const id = o.id || o, nm = o.name || o;
            const isSelected = String(value) === String(id);
            return (
              <div key={i}
                onClick={() => { onChange(id); setOpen(false); setQ(""); }}
                style={{
                  padding:"9px 12px", fontSize:13.5, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:10,
                  background: isSelected ? "#EFF6FF" : "transparent",
                  borderBottom:"1px solid #F8FAFC",
                  fontFamily:"'Poppins', sans-serif",
                }}>
                <Avatar name={nm} size={28} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, color:"#1E293B", fontSize:13.5 }}>{nm}</div>
                  {o.mobile && <div style={{ fontSize:11.5, color:"#64748B" }}>{o.mobile}{o.city ? ` · ${o.city}` : ""}</div>}
                </div>
                {isSelected && <span style={{ color:BR, fontWeight:800 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── SD ──────────────────────────────────────────────────────────────────
function SD({ options, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter(o => (o.name || o).toString().toLowerCase().includes(q.toLowerCase()));
  const label    = options.find(o => (o.id !== undefined ? o.id : o) === value);
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <input
        style={{
          ...inp, paddingRight:32, cursor:"pointer",
          ...(open ? { borderColor:BR, boxShadow:"0 0 0 3px rgba(29,78,216,.10)" } : {}),
        }}
        value={open ? q : label ? (label.name || label) : ""}
        placeholder={loading ? "Loading..." : ""}
        onFocus={() => setOpen(true)}
        onChange={e => setQ(e.target.value)}
        readOnly={!open}
      />
      <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:9, color:"#94A3B8", pointerEvents:"none" }}>{open ? "▲" : "▼"}</span>
      {open && (
        <div style={{ position:"absolute", top:46, left:0, right:0, background:"#fff", border:"1.5px solid #CBD5E1", borderRadius:10, zIndex:9999, boxShadow:"0 8px 24px rgba(0,0,0,.12)", maxHeight:200, overflowY:"auto" }}>
          {filtered.map((o, i) => {
            const id = o.id !== undefined ? o.id : o;
            const nm = o.name || o;
            return (
              <div key={i} onClick={() => { onChange(id); setOpen(false); setQ(""); }}
                style={{ padding:"9px 12px", fontSize:13.5, cursor:"pointer", background:value === id ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F8FAFC", fontFamily:"'Poppins', sans-serif" }}>
                {nm}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding:10, color:"#94A3B8", textAlign:"center", fontSize:13, fontFamily:"'Poppins', sans-serif" }}>No results</div>}
        </div>
      )}
    </div>
  );
}

// ─── EmpSD ───────────────────────────────────────────────────────────────
function EmpSD({ options, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter(o => (o.name || o).toString().toLowerCase().includes(q.toLowerCase()));
  const selected = options.find(o => (o.id || o) === value);
  const selName  = selected ? (selected.name || selected) : "";
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div onClick={() => setOpen(!open)}
        style={{
          ...inp, display:"flex", alignItems:"center", gap:8, cursor:"pointer",
          paddingRight:32, borderColor: open ? BR : "#CBD5E1",
          ...(open ? { boxShadow:"0 0 0 3px rgba(29,78,216,.10)" } : {}),
        }}>
        {selName
          ? <><Avatar name={selName} size={26} /><span style={{ fontSize:14, color:"#1E293B", flex:1, fontFamily:"'Poppins', sans-serif" }}>{selName}</span></>
          : <span style={{ fontSize:14, color:"#94A3B8", flex:1, fontFamily:"'Poppins', sans-serif" }}>{loading ? "Loading..." : ""}</span>
        }
        <span style={{ position:"absolute", right:12, fontSize:9, color:"#94A3B8" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ position:"absolute", top:46, left:0, right:0, background:"#fff", border:"1.5px solid #CBD5E1", borderRadius:10, zIndex:9999, boxShadow:"0 8px 24px rgba(0,0,0,.12)", maxHeight:220, overflowY:"auto" }}>
          <input
            style={{ width:"100%", padding:"9px 12px", border:"none", borderBottom:"1px solid #F1F5F9", outline:"none", fontSize:13, fontFamily:"'Poppins', sans-serif", boxSizing:"border-box" }}
            placeholder="" value={q} onChange={e => setQ(e.target.value)}
            onClick={e => e.stopPropagation()} autoFocus
          />
          {filtered.map((o, i) => {
            const id = o.id || o, nm = o.name || o;
            return (
              <div key={i} onClick={() => { onChange(id); setOpen(false); setQ(""); }}
                style={{ padding:"9px 12px", fontSize:13.5, cursor:"pointer", display:"flex", alignItems:"center", gap:9, background: value === id ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F8FAFC", fontFamily:"'Poppins', sans-serif" }}>
                <Avatar name={nm} size={28} />
                <span style={{ flex:1 }}>{nm}</span>
                {value === id && <span style={{ color:BR, fontWeight:800 }}>✓</span>}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding:10, color:"#94A3B8", textAlign:"center", fontSize:13, fontFamily:"'Poppins', sans-serif" }}>No results</div>}
        </div>
      )}
    </div>
  );
}

// ─── CommSD ───────────────────────────────────────────────────────────────
function CommSD({ options, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter(o => (o.name || o).toString().toLowerCase().includes(q.toLowerCase()));
  const selected = options.find(o => (o.id || o) === value);
  const selName  = selected ? (selected.name || selected) : "";
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div style={{ position:"relative" }}>
        {selName && !open && (
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:17, pointerEvents:"none", zIndex:1 }}>
            {getCommIcon(selName)}
          </span>
        )}
        <input
          style={{
            ...inp,
            paddingLeft:  selName && !open ? 36 : 12,
            paddingRight: 32,
            cursor: "text",
            borderColor: open ? BR : "#CBD5E1",
            ...(open ? { boxShadow:"0 0 0 3px rgba(29,78,216,.10)" } : {}),
          }}
          value={open ? q : selName}
          placeholder={loading ? "Loading..." : ""}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
        />
        <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:9, color:"#94A3B8", pointerEvents:"none" }}>
          {open ? "▲" : "▼"}
        </span>
      </div>
      {open && (
        <div style={{ position:"absolute", top:46, left:0, right:0, background:"#fff", border:"1.5px solid #CBD5E1", borderRadius:10, zIndex:9999, boxShadow:"0 8px 24px rgba(0,0,0,.12)", maxHeight:220, overflowY:"auto" }}>
          {filtered.map((o, i) => {
            const id = o.id || o, nm = o.name || o;
            return (
              <div key={i}
                onClick={() => { onChange(id); setOpen(false); setQ(""); }}
                style={{ padding:"9px 12px", fontSize:13.5, cursor:"pointer", display:"flex", alignItems:"center", gap:9, background: value === id ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F8FAFC", fontFamily:"'Poppins', sans-serif" }}>
                <span style={{ fontSize:18, width:24, textAlign:"center", flexShrink:0 }}>{getCommIcon(nm)}</span>
                <span style={{ flex:1 }}>{nm}</span>
                {value === id && <span style={{ color:BR, fontWeight:800 }}>✓</span>}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding:10, color:"#94A3B8", textAlign:"center", fontSize:13, fontFamily:"'Poppins', sans-serif" }}>No results</div>}
        </div>
      )}
    </div>
  );
}

// ─── MS ──────────────────────────────────────────────────────────────────
function MS({ options, value = [], onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle   = id => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  const filtered = options.filter(o => (o.name || o).toLowerCase().includes(q.toLowerCase()));
  const getLabel = id => { const f = options.find(o => (o.id || o) === id); return f ? (f.name || f) : id; };
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div onClick={() => setOpen(!open)}
        style={{
          border: open ? `1.5px solid ${BR}` : "1.5px solid #CBD5E1",
          borderRadius:8, minHeight:42, padding:"5px 10px", background:"#fff",
          display:"flex", flexWrap:"wrap", gap:5, cursor:"pointer", alignItems:"center",
          ...(open ? { boxShadow:"0 0 0 3px rgba(29,78,216,.10)" } : {}),
          fontFamily:"'Poppins', sans-serif",
        }}>
        {value.length
          ? value.map(v => (
              <span key={v} style={{ background:BR, color:"#fff", fontSize:12, padding:"3px 9px", borderRadius:5, display:"flex", alignItems:"center", gap:4, fontWeight:600, fontFamily:"'Poppins', sans-serif" }}>
                {getLabel(v)}<span onClick={e => { e.stopPropagation(); toggle(v); }} style={{ cursor:"pointer", fontWeight:900 }}>×</span>
              </span>
            ))
          : <span style={{ color:"#94A3B8", fontSize:14, fontFamily:"'Poppins', sans-serif" }}>{loading ? "Loading..." : ""}</span>}
        <span style={{ marginLeft:"auto", fontSize:9, color:"#94A3B8" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ position:"absolute", top:46, left:0, right:0, background:"#fff", border:"1.5px solid #CBD5E1", borderRadius:10, zIndex:9999, boxShadow:"0 8px 24px rgba(0,0,0,.12)", maxHeight:200, overflowY:"auto" }}>
          <input
            style={{ width:"100%", padding:"9px 12px", border:"none", borderBottom:"1px solid #F1F5F9", outline:"none", fontSize:13, fontFamily:"'Poppins', sans-serif", boxSizing:"border-box" }}
            placeholder="" value={q} onChange={e => setQ(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
          {filtered.map((o, i) => {
            const id = o.id || o, nm = o.name || o;
            return (
              <div key={i} onClick={() => toggle(id)}
                style={{ padding:"9px 12px", fontSize:13.5, cursor:"pointer", display:"flex", alignItems:"center", gap:8, background:value.includes(id) ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F8FAFC", fontFamily:"'Poppins', sans-serif" }}>
                <span style={{ fontSize:15, flexShrink:0 }}>📍</span>
                <span style={{ flex:1 }}>{nm}</span>
                {value.includes(id) && <span style={{ color:BR, fontWeight:800 }}>✓</span>}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding:10, color:"#94A3B8", textAlign:"center", fontSize:13, fontFamily:"'Poppins', sans-serif" }}>No results</div>}
        </div>
      )}
    </div>
  );
}

// ─── CustomerModal ────────────────────────────────────────────────────────
function CustomerModal({ onSave, onClose }) {
  const [f, setF]           = useState({ name:"", mobile:"", mobile2:"", email:"", gender:"", dob:"", designation:"", city:"", state:"", pincode:"", address:"" });
  const [saving, setSaving] = useState(false);
  const set = k => v => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name?.trim())   { toast.error("Name required!");   return; }
    if (!f.mobile?.trim()) { toast.error("Mobile required!"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${URL}/customer-masters`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerName: f.name, mobileno: f.mobile, secondaryMobileno: f.mobile2 || null,
          emailid: f.email || null, gender: f.gender || null,
          birthDate: f.dob ? new Date(f.dob).toISOString() : null,
          designation: f.designation || null, pincode: f.pincode || null,
          city: f.city || null, state: f.state || null, fullAddress: f.address || null,
          isActive: 1, isOn: 1, isDefault: 0, lastChanged: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error("Server error " + res.status);
      const data = await res.json();
      toast.success("Customer saved successfully! 👤");
      onSave({ id: data.customerId, name: f.name, mobile: f.mobile, email: f.email, city: f.city, designation: f.designation });
    } catch (e) { toast.error("Save failed: " + e.message); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(7,40,119,.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, padding:12, backdropFilter:"blur(3px)" }}>
      <div style={{ background:"#fff", width:"100%", maxWidth:700, borderRadius:14, overflow:"hidden", boxShadow:"0 24px 48px rgba(7,40,119,.2)", display:"flex", flexDirection:"column", maxHeight:"90vh", fontFamily:"'Poppins', sans-serif" }}>
        <div style={{ background:DK, padding:"16px 20px", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:800, fontSize:15, fontFamily:"'Poppins', sans-serif" }}>👤 NEW CUSTOMER</span>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15 }}>✕</button>
        </div>
        <div style={{ padding:18, overflowY:"auto", display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <label style={lbl}>Full Name <span style={{ color:"#DC2626" }}>*</span></label>
            <input style={inp} value={f.name} onChange={e => set("name")(e.target.value)} />
          </div>
          <div style={g(2)}>
            <div><label style={lbl}>Mobile No <span style={{ color:"#DC2626" }}>*</span></label><input style={inp} value={f.mobile} onChange={e => set("mobile")(e.target.value)} /></div>
            <div><label style={lbl}>Alt. Mobile</label><input style={inp} value={f.mobile2} onChange={e => set("mobile2")(e.target.value)} /></div>
            <div><label style={lbl}>Email</label><input style={inp} value={f.email} onChange={e => set("email")(e.target.value)} /></div>
            <div><label style={lbl}>Gender</label><SD options={["Male","Female","Other"]} value={f.gender} onChange={set("gender")} /></div>
            <div><label style={lbl}>Birth Date</label><input style={inp} type="date" value={f.dob} onChange={e => set("dob")(e.target.value)} /></div>
            <div><label style={lbl}>Designation</label><input style={inp} value={f.designation} onChange={e => set("designation")(e.target.value)} /></div>
            <div><label style={lbl}>City</label><input style={inp} value={f.city} onChange={e => set("city")(e.target.value)} /></div>
            <div><label style={lbl}>State</label><input style={inp} value={f.state} onChange={e => set("state")(e.target.value)} /></div>
            <div><label style={lbl}>Pincode</label><input style={inp} value={f.pincode} onChange={e => set("pincode")(e.target.value)} /></div>
          </div>
          <div>
            <label style={lbl}>Full Address</label>
            <textarea style={{ ...inp, height:"auto", minHeight:68, padding:"10px 12px", resize:"vertical" }} value={f.address} onChange={e => set("address")(e.target.value)} />
          </div>
        </div>
        <div style={{ padding:"12px 18px", borderTop:"1px solid #E2E8F0", display:"flex", justifyContent:"flex-end", gap:10, background:"#F8FAFC" }}>
          <button onClick={onClose} style={{ padding:"9px 20px", borderRadius:50, border:"1.5px solid #CBD5E1", background:"#fff", fontSize:13.5, fontWeight:700, cursor:"pointer", color:"#64748B", fontFamily:"'Poppins', sans-serif" }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding:"9px 24px", borderRadius:50, border:"none", background:BR, color:"#fff", fontSize:13.5, fontWeight:700, cursor:saving?"not-allowed":"pointer", opacity:saving?.7:1, fontFamily:"'Poppins', sans-serif" }}>
            {saving ? "Saving..." : "💾 Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AreaModal ────────────────────────────────────────────────────────────
function AreaModal({ onSave, onClose }) {
  const [areaName, setAreaName] = useState("");
  const [saving, setSaving]     = useState(false);

  const save = async () => {
    if (!areaName.trim()) { toast.error("Area name required!"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${URL}/area-masters`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          areaName: areaName.trim(),
          isActive: 1,
          lastChanged: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error("Server error " + res.status);
      const data = await res.json();
      toast.success("Area saved successfully! 📍");
      onSave({ id: data.areaId, name: areaName.trim() });
    } catch (e) { toast.error("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(7,40,119,.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, padding:12, backdropFilter:"blur(3px)" }}>
      <div style={{ background:"#fff", width:"100%", maxWidth:420, borderRadius:14, overflow:"hidden", boxShadow:"0 24px 48px rgba(7,40,119,.2)", display:"flex", flexDirection:"column", fontFamily:"'Poppins', sans-serif" }}>
        <div style={{ background:DK, padding:"16px 20px", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:800, fontSize:15, fontFamily:"'Poppins', sans-serif" }}>📍 NEW AREA</span>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15 }}>✕</button>
        </div>
        <div style={{ padding:20 }}>
          <label style={lbl}>Area Name <span style={{ color:"#DC2626" }}>*</span></label>
          <input
            style={inp}
            value={areaName}
            onChange={e => setAreaName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            autoFocus
          />
        </div>
        <div style={{ padding:"12px 18px", borderTop:"1px solid #E2E8F0", display:"flex", justifyContent:"flex-end", gap:10, background:"#F8FAFC" }}>
          <button onClick={onClose} style={{ padding:"9px 20px", borderRadius:50, border:"1.5px solid #CBD5E1", background:"#fff", fontSize:13.5, fontWeight:700, cursor:"pointer", color:"#64748B", fontFamily:"'Poppins', sans-serif" }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding:"9px 24px", borderRadius:50, border:"none", background:BR, color:"#fff", fontSize:13.5, fontWeight:700, cursor:saving?"not-allowed":"pointer", opacity:saving?.7:1, fontFamily:"'Poppins', sans-serif" }}>
            {saving ? "Saving..." : "💾 Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StepBar ──────────────────────────────────────────────────────────────
const STEPS = [{ label: "Enquiry Details", num: "01" }, { label: "Lead Status", num: "02" }];

function StepBar({ step, isEdit }) {
  const pct = step === 1 ? 50 : 100;
  return (
    <>
      <style>{`
        .sb-wrap {
          background: #0d1b3e;
          background-image:
            repeating-linear-gradient(0deg,  transparent, transparent 39px, rgba(255,255,255,0.018) 39px, rgba(255,255,255,0.018) 40px),
            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.018) 39px, rgba(255,255,255,0.018) 40px);
          font-family: 'Poppins', sans-serif;
          padding: 0 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          min-height: 76px;
          flex-wrap: nowrap;
        }
        .sb-left { flex-shrink: 0; padding: 14px 0; }
        .sb-left-sup { font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(148,163,184,0.65); margin-bottom: 3px; }
        .sb-left-title { font-size: 17px; font-weight: 700; color: #fff; line-height: 1.2; white-space: nowrap; }
        .sb-center { display: flex; align-items: center; flex: 0 1 auto; }
        .sb-step { display: flex; align-items: center; gap: 10px; }
        .sb-circle { width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.3s; }
        .sb-circle-active { background: #fff; border: 2px solid #fff; }
        .sb-circle-done   { background: rgba(29,78,216,0.45); border: 2px solid rgba(29,78,216,0.8); }
        .sb-circle-idle   { background: rgba(255,255,255,0.055); border: 2px solid rgba(255,255,255,0.18); }
        .sb-num-active { font-size: 13px; font-weight: 700; color: #0d1b3e; letter-spacing: 0.5px; }
        .sb-num-idle   { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.3); letter-spacing: 0.5px; }
        .sb-check      { font-size: 14px; color: #fff; font-weight: 700; }
        .sb-label-active { font-size: 13.5px; font-weight: 700; color: #fff; white-space: nowrap; }
        .sb-label-done   { font-size: 13.5px; font-weight: 400; color: rgba(255,255,255,0.55); white-space: nowrap; }
        .sb-label-idle   { font-size: 13.5px; font-weight: 400; color: rgba(255,255,255,0.28); white-space: nowrap; }
        .sb-connector { width: 52px; height: 2px; margin: 0 14px; border-radius: 2px; flex-shrink: 0; }
        .sb-connector-done { background: rgba(29,78,216,0.5); }
        .sb-connector-idle { background: rgba(255,255,255,0.1); }
        .sb-right { flex-shrink: 0; padding: 14px 0; text-align: right; }
        .sb-right-sup { font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(148,163,184,0.65); margin-bottom: 7px; text-align: right; }
        .sb-right-row { display: flex; align-items: center; gap: 10px; justify-content: flex-end; }
        .sb-bar-track { width: 100px; height: 3px; border-radius: 3px; background: rgba(255,255,255,0.1); overflow: hidden; }
        .sb-bar-fill  { height: 100%; border-radius: 3px; background: #fff; transition: width 0.4s ease; }
        .sb-pct { font-size: 13px; font-weight: 600; color: #fff; min-width: 34px; text-align: right; }

        @media (max-width: 700px) {
          .sb-wrap { flex-wrap: wrap; padding: 14px 16px; gap: 10px; min-height: unset; }
          .sb-left { padding: 0; }
          .sb-left-title { font-size: 15px; }
          .sb-right { text-align: left; width: 100%; padding: 0; }
          .sb-right-row { justify-content: flex-start; }
          .sb-right-sup { text-align: left; }
          .sb-bar-track { width: 80px; }
          .sb-connector { width: 32px; margin: 0 8px; }
        }
        @media (max-width: 480px) {
          .sb-label-active, .sb-label-done, .sb-label-idle { font-size: 12px; }
          .sb-circle { width: 36px; height: 36px; }
          .sb-connector { width: 24px; margin: 0 6px; }
        }
        @media (max-width: 640px) {
          .grid-2  { grid-template-columns: 1fr !important; }
          .grid-4  { grid-template-columns: repeat(2, 1fr) !important; }
          .grid-2s { grid-template-columns: 1fr !important; }
          .note-half { width: 100% !important; }
          .modal-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 400px) {
          .grid-4 { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="sb-wrap">
        <div className="sb-left">
          <div className="sb-left-sup">LEAD MANAGEMENT</div>
          <div className="sb-left-title">{isEdit ? "✏️ Edit Enquiry" : "New Lead / Enquiry"}</div>
        </div>

        <div className="sb-center">
          {STEPS.map((s, i) => {
            const num = i + 1;
            const active = num === step;
            const done   = num < step;
            return (
              <React.Fragment key={i}>
                <div className="sb-step">
                  <div className={`sb-circle ${active ? "sb-circle-active" : done ? "sb-circle-done" : "sb-circle-idle"}`}>
                    {done
                      ? <span className="sb-check">✓</span>
                      : <span className={active ? "sb-num-active" : "sb-num-idle"}>{s.num}</span>
                    }
                  </div>
                  <span className={active ? "sb-label-active" : done ? "sb-label-done" : "sb-label-idle"}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`sb-connector ${done ? "sb-connector-done" : "sb-connector-idle"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="sb-right">
          <div className="sb-right-sup">PROGRESS</div>
          <div className="sb-right-row">
            <div className="sb-bar-track">
              <div className="sb-bar-fill" style={{ width:`${pct}%` }} />
            </div>
            <span className="sb-pct">{pct}%</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── SecTitle ─────────────────────────────────────────────────────────────
function SecTitle({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, color:"#64748B", letterSpacing:"1.5px", textTransform:"uppercase", whiteSpace:"nowrap", fontFamily:"'Poppins', sans-serif" }}>{children}</span>
      <div style={{ flex:1, height:1, background:"#E2E8F0" }} />
    </div>
  );
}

// ─── Btn ──────────────────────────────────────────────────────────────────
function Btn({ children, onClick, variant="primary", style:extra={}, disabled=false }) {
  const base = { border:"none", borderRadius:50, fontFamily:"'Poppins', sans-serif", fontWeight:700, cursor:disabled?"not-allowed":"pointer", fontSize:14, display:"inline-flex", alignItems:"center", gap:6, opacity:disabled?.65:1, transition:"opacity .15s, transform .1s" };
  const variants = {
    primary: { background:BR,         color:"#fff",    padding:"10px 26px" },
    outline: { background:"#fff",    color:BR,        border:`2px solid ${BR}`, padding:"8px 20px" },
    success: { background:"#16A34A", color:"#fff",    padding:"10px 26px" },
    danger:  { background:"#DC2626", color:"#fff",    padding:"10px 26px" },
    ghost:   { background:"#fff",    color:"#64748B", border:"1.5px solid #CBD5E1", padding:"8px 18px" },
  };
  return <button onClick={!disabled ? onClick : undefined} style={{ ...base, ...variants[variant], ...extra }}>{children}</button>;
}

// ─── Safe JSON helper ─────────────────────────────────────────────────────
const safeJson = async (res) => {
  const text = await res.text();
  if (!text || text.trim() === "") return null;
  try { return JSON.parse(text); }
  catch { console.error("Invalid JSON from server:", text.slice(0, 200)); return null; }
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────
const LeadEnquiry = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editData = location.state?.editEnquiry || null;
  const isEdit   = !!editData;

  const [step,          setStep]          = useState(1);
  const [showModal,     setShowModal]     = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [submitting,    setSubmitting]    = useState(false);

  const [customers,     setCustomers]     = useState([]);
  const [sources,       setSources]       = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [budgets,       setBudgets]       = useState([]);
  const [goals,         setGoals]         = useState([]);
  const [timelines,     setTimelines]     = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [areas,         setAreas]         = useState([]);
  const [commModes,     setCommModes]     = useState([]);
  const [leadStatuses,  setLeadStatuses]  = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [custId,        setCustId]        = useState("");

  // ── Initial state factories ──────────────────────────────────────────
  const iS1 = () => ({
    leadDate: today(), sourceId:"", propertyTypeId:"", budgetId:"",
    goalId:"", timelineId:"", employeeRefId:"", areaIds:[], note:"",
  });

  const iS2 = () => ({
    fkLeadStatusId:"", assignTo:"", commMode:"",
    callLeadDate: today(), callLeadTime: nowTime(),
    leadFrom:"", priority:"",
  });

  const [s1, setS1] = useState(iS1());
  const [s2, setS2] = useState(iS2());

  const cust = customers.find(c => String(c.id) === String(custId));

  const selectedStatusName = leadStatuses.find(l => l.id === s2.fkLeadStatusId)?.name?.toLowerCase() || "";
  const isFollow  = selectedStatusName.includes("follow");
  const isSuccess = selectedStatusName.includes("success");
  const isCancel  = selectedStatusName.includes("cancel");

  // ── Fetch masters ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setLoadingMaster(true);
      try {
        const af = encodeURIComponent(JSON.stringify({ where: { isActive: 1 } }));
        const cf = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, fields: ["customerId","customerName","mobileno","emailid","city","designation"] }));

        const [custRes, srcRes, propRes, budRes, goalRes, tlRes, empRes, areaRes, commRes, lsRes] = await Promise.all([
          fetch(`${URL}/customer-masters?filter=${cf}`),
          fetch(`${URL}/source-masters?filter=${af}`),
          fetch(`${URL}/propertytype-masters?filter=${af}`),
          fetch(`${URL}/budget-masters?filter=${af}`),
          fetch(`${URL}/primary-goals?filter=${af}`),
          fetch(`${URL}/timeline-masters?filter=${af}`),
          fetch(`${URL}/employee-masters?filter=${af}`),
          fetch(`${URL}/area-masters?filter=${af}`),
          fetch(`${URL}/communicationmedia-masters?filter=${af}`),
          fetch(`${URL}/lead-status-masters?filter=${af}`),
        ]);

        const [custD, srcD, propD, budD, goalD, tlD, empD, areaD, commD, lsD] = await Promise.all([
          custRes.json(), srcRes.json(), propRes.json(), budRes.json(),
          goalRes.json(), tlRes.json(), empRes.json(), areaRes.json(),
          commRes.json(), lsRes.json(),
        ]);

        setCustomers(custD.map(c     => ({ id: c.customerId,           name: c.customerName,          mobile: c.mobileno, email: c.emailid, city: c.city, designation: c.designation })));
        setSources(srcD.map(r        => ({ id: r.sourceId,             name: r.sourceName             })));
        setPropertyTypes(propD.map(r => ({ id: r.propertytypeId,       name: r.propertytypeName       })));
        setBudgets(budD.map(r        => ({ id: r.budgetId,             name: r.budgetName             })));
        setGoals(goalD.map(r         => ({ id: r.primarygoalId,        name: r.primarygoalName        })));
        setTimelines(tlD.map(r       => ({ id: r.timelineId,           name: r.timelineName           })));
        setEmployees(empD.map(r      => ({ id: r.employeeId,           name: r.employeeName           })));
        setAreas(areaD.map(r         => ({ id: r.areaId,               name: r.areaName               })));
        setCommModes(commD.map(r     => ({ id: r.communicationmediaId, name: r.communicationmediaName })));
        setLeadStatuses(lsD.map(r    => ({ id: r.statusId,             name: r.statusName             })));

        // ── Pre-fill for edit mode ──
        if (editData) {
          const ec = editData.customer || {};
          if (ec.customerId) setCustId(String(ec.customerId));

          setS1({
            leadDate:       editData.enquiryDate
                              ? new Date(editData.enquiryDate).toISOString().split("T")[0]
                              : today(),
            sourceId:       editData.source?.sourceId             ?? "",
            propertyTypeId: editData.propertyType?.propertytypeId ?? "",
            budgetId:       editData.budget?.budgetId             ?? "",
            goalId:         editData.primaryGoal?.primarygoalId   ?? "",
            timelineId:     editData.timeline?.timelineId         ?? "",
            employeeRefId:  editData.employee?.employeeId         ?? "",
            areaIds:        editData.areas?.map(a => a.areaId)    || [],
            note:           editData.note                         || "",
          });

          const latestFollowup = editData.followups?.[0];
          if (latestFollowup) {
            setS2({
              fkLeadStatusId: latestFollowup.fkLeadStatusId || latestFollowup.status?.statusId || "",
              assignTo:       latestFollowup.assignTo       || "",
              commMode:       editData.communicationMedia?.communicationmediaId
                                || latestFollowup.fkcommunicationmediaId || "",
              callLeadDate:   latestFollowup.callLeadDate
                                ? new Date(latestFollowup.callLeadDate).toISOString().split("T")[0]
                                : today(),
              callLeadTime:   latestFollowup.callLeadTime || nowTime(),
              leadFrom:       "",
              priority:       latestFollowup.priority ?? "",
            });
          }
        }

      } catch (e) { console.error("Master fetch error:", e); }
      finally { setLoadingMaster(false); }
    };
    fetchAll();
  }, []);

  const addCust = c => { setCustomers(p => [...p, c]); setCustId(c.id); setShowModal(false); };

  const addArea = a => {
    setAreas(p => [...p, a]);
    setS1(prev => ({ ...prev, areaIds: [...prev.areaIds, a.id] }));
    setShowAreaModal(false);
  };

  const saveNext = () => {
    if (!custId)      { toast.error("Please select or add a customer!"); return; }
    if (!s1.leadDate) { toast.error("Please select lead date!");         return; }
    setStep(2);
  };

  const saveLead = async () => {
    // ── Validations ──
    if (!s2.fkLeadStatusId) {
      toast.error("Please select Lead Status!");
      return;
    }
    // Priority फक्त Follow-up ला required आहे
    if (isFollow && (!s2.priority && s2.priority !== 0)) {
      toast.error("Please select Priority!");
      return;
    }
    if (isFollow && (!s2.assignTo || !s2.commMode)) {
      toast.error("Fill Assign To & Comm. Mode!");
      return;
    }

    setSubmitting(true);

    try {
      const now = new Date().toISOString();

      // ── logged-in employee ID ──
      const loggedInUserId = getLoggedInUserId();

      // ── Enquiry payload ──
      const rawEnqPayload = {
        enquiryDate:            s1.leadDate ? new Date(s1.leadDate).toISOString() : null,
        fkCustomerId:           Number(custId),
        fkSourceId:             s1.sourceId       ? Number(s1.sourceId)       : null,
        fkPropertytypeId:       s1.propertyTypeId ? Number(s1.propertyTypeId) : null,
        fkBudgetId:             s1.budgetId       ? Number(s1.budgetId)       : null,
        fkPrimarygoalId:        s1.goalId         ? Number(s1.goalId)         : null,
        fkTimelineId:           s1.timelineId     ? Number(s1.timelineId)     : null,
        fkEmployeeId:           s1.employeeRefId  ? Number(s1.employeeRefId)  : null,
        fkAreaId:               s1.areaIds.length > 0 ? s1.areaIds.join(",") : null,
        fkCommunicationmediaId: s2.commMode       ? Number(s2.commMode)       : null,
        note:                   s1.note || null,
        // नवीन record ला createdBy save करा, edit ला नाही
        ...(!isEdit && loggedInUserId ? { createdBy: Number(loggedInUserId) } : {}),
        lastChanged:            now,
      };

      const enqPayload = Object.fromEntries(
        Object.entries(rawEnqPayload).filter(([, v]) =>
          v !== null && v !== "" && v !== undefined && !(typeof v === "number" && isNaN(v))
        )
      );

      const enqMethod = isEdit ? "PATCH" : "POST";
      const enqUrl    = isEdit ? `${URL}/enquiries/${editData.enquiryId}` : `${URL}/enquiries`;

      const enqRes = await fetch(enqUrl, {
        method:  enqMethod,
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(enqPayload),
      });

      if (!enqRes.ok) {
        const errData = await safeJson(enqRes);
        console.error("Enquiry Error:", errData);
        throw new Error(errData?.error || errData?.message || `Enquiry save failed (${enqRes.status})`);
      }

      const enqData   = await safeJson(enqRes);
      const enquiryId = isEdit
        ? editData.enquiryId
        : (enqData?.enquiryId ?? enqData?.id);

      // ── Followup payload ──
      const rawFollowupPayload = {
        fkcustomerId:           Number(custId),
        fkenquiryId:            Number(enquiryId),
        fkLeadStatusId:         Number(s2.fkLeadStatusId),
        assignTo:               s2.assignTo ? Number(s2.assignTo)  : null,
        fkcommunicationmediaId: s2.commMode ? Number(s2.commMode)  : null,
     callLeadDate: isFollow && s2.callLeadDate ? s2.callLeadDate + "T00:00:00.000+05:30" : null,
        callLeadTime:           isFollow ? s2.callLeadTime : null,
        // Priority फक्त Follow-up ला save करा
        priority: isFollow ? Number(s2.priority) : 2,
        // नवीन record ला createdBy save करा, edit ला नाही
        ...(!isEdit && loggedInUserId ? { createdBy: Number(loggedInUserId) } : {}),
        lastChanged:            now,
      };

      const followupPayload = Object.fromEntries(
        Object.entries(rawFollowupPayload).filter(([, v]) =>
          v !== null && v !== "" && v !== undefined && !(typeof v === "number" && isNaN(v))
        )
      );

      let followupRes;
      if (isEdit && editData.followups?.[0]?.followupId) {
        followupRes = await fetch(`${URL}/followup-masters/${editData.followups[0].followupId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(followupPayload),
        });
      } else {
        followupRes = await fetch(`${URL}/followup-masters`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(followupPayload),
        });
      }

      if (!followupRes.ok) {
        const followErr = await safeJson(followupRes);
        console.error("Followup Error:", followErr);
        throw new Error(followErr?.error || followErr?.message || `Followup save failed (${followupRes.status})`);
      }

      toast.success(isEdit ? "Enquiry Updated! ✅" : "Lead Saved! ✅");
      navigate("/inquiry-details");

    } catch (e) {
      console.error("saveLead Error:", e);
      toast.error("Error: " + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{ width:"100%", margin:0, padding:"0 0 40px", fontFamily:"'Poppins', sans-serif", background:"#F1F5F9", minHeight:"100vh" }}>

      <Toaster
        position="top-right"
        toastOptions={{ style: { fontFamily:"'Poppins', sans-serif", fontSize:13.5, borderRadius:10 } }}
      />

      <StepBar step={step} isEdit={isEdit} />

      {isEdit && (
        <div style={{
          background:"#FEF9C3", borderBottom:"1px solid #FDE68A",
          padding:"10px 24px", display:"flex", alignItems:"center",
          gap:10, fontSize:13.5, color:"#854D0E", fontWeight:600, flexWrap:"wrap",
        }}>
          <span>✏️ Editing Enquiry #{editData.enquiryId}</span>
          <button
            onClick={() => navigate("/inquiry-details")}
            style={{ marginLeft:"auto", padding:"5px 14px", borderRadius:20, border:"1.5px solid #D97706", background:"#fff", cursor:"pointer", fontFamily:"'Poppins', sans-serif" }}>
            ✕ Cancel Edit
          </button>
        </div>
      )}

      <div style={{ padding:"16px" }}>

        {/* ══════════ STEP 1 ══════════ */}
        {step === 1 && (
          <div style={card}>
            <div style={cardBody}>
              <SecTitle>Customer &amp; Date</SecTitle>

              <div className="grid-2" style={{ ...g(2), marginBottom:16 }}>
                <div>
                  <label style={lbl}>Customer Name <span style={{ color:"#DC2626" }}>*</span></label>
                  <div style={{ display:"flex", gap:8 }}>
                    <div style={{ flex:1 }}>
                      <CustomerSD
                        options={customers} value={custId} onChange={setCustId}
                        loading={loadingMaster}
                      />
                    </div>
                    {!isEdit && (
                      <button onClick={() => setShowModal(true)}
                        style={{ background:BR, color:"#fff", border:"none", width:42, height:42, borderRadius:8, cursor:"pointer", fontSize:22, fontWeight:"bold", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
                    )}
                  </div>
                </div>
                <div>
                  <label style={lbl}>Enquiry / Lead Date <span style={{ color:"#DC2626" }}>*</span></label>
                  <input style={inp} type="date" value={s1.leadDate} onChange={e => setS1({ ...s1, leadDate:e.target.value })} />
                </div>
              </div>

              {cust && (
                <div style={custBar}>
                  <div>📱 {cust.mobile}</div>
                  <div>✉️ {cust.email || "N/A"}</div>
                  <div>🏙️ {cust.city || "N/A"}</div>
                  <div>💼 {cust.designation || "N/A"}</div>
                </div>
              )}

              <div style={divider} />
              <SecTitle>Enquiry Details</SecTitle>

              <div className="grid-4" style={{ ...g(4), marginBottom:14 }}>
                <div>
                  <label style={lbl}>Source</label>
                  <SD options={sources} value={s1.sourceId} onChange={v => setS1({...s1,sourceId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Property Type</label>
                  <SD options={propertyTypes} value={s1.propertyTypeId} onChange={v => setS1({...s1,propertyTypeId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Budget</label>
                  <SD options={budgets} value={s1.budgetId} onChange={v => setS1({...s1,budgetId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Primary Goal</label>
                  <SD options={goals} value={s1.goalId} onChange={v => setS1({...s1,goalId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Timeline</label>
                  <SD options={timelines} value={s1.timelineId} onChange={v => setS1({...s1,timelineId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Employee Ref.</label>
                  <EmpSD options={employees} value={s1.employeeRefId} onChange={v => setS1({...s1,employeeRefId:v})} loading={loadingMaster} />
                </div>
                <div>
                  <label style={lbl}>Area of Interest</label>
                  <div style={{ display:"flex", gap:8 }}>
                    <div style={{ flex:1 }}>
                      <MS options={areas} value={s1.areaIds} onChange={v => setS1({...s1,areaIds:v})} loading={loadingMaster} />
                    </div>
                    <button
                      onClick={() => setShowAreaModal(true)}
                      style={{ background:BR, color:"#fff", border:"none", width:42, height:42, borderRadius:8, cursor:"pointer", fontSize:22, fontWeight:"bold", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
                  </div>
                </div>
              </div>

              <div className="note-half" style={{ width:"50%" }}>
                <label style={lbl}>Note / Remark</label>
                <textarea
                  style={{ ...inp, height:"auto", minHeight:52, padding:"8px 12px", resize:"vertical" }}
                  value={s1.note}
                  onChange={e => setS1({...s1, note:e.target.value})}
                />
              </div>

              <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", marginTop:20, gap:10, flexWrap:"wrap" }}>
                <Btn variant="outline" onClick={() => navigate("/inquiry-details")}>📋 List</Btn>
                <Btn onClick={saveNext}>Save &amp; Next →</Btn>
              </div>
            </div>
          </div>
        )}

        {/* ══════════ STEP 2 ══════════ */}
        {step === 2 && (
          <div style={card}>
            <div style={cardBody}>
              <div style={custBar}>
                <div>👤 {cust ? cust.name : "—"}</div>
                <div>📅 {s1.leadDate}</div>
              </div>

              <SecTitle>Lead Status</SecTitle>

              {/* Lead Status — full width जेव्हा Follow-up नाही, half width जेव्हा Follow-up आहे */}
              <div className="grid-2s" style={{ ...g(2), marginBottom:20 }}>
                <div>
                  <label style={lbl}>Select Lead Status <span style={{ color:"#DC2626" }}>*</span></label>
                  <SD
                    options={leadStatuses}
                    value={s2.fkLeadStatusId}
                    onChange={v => setS2({...s2, fkLeadStatusId:v, priority:""})}
                    loading={loadingMaster}
                  />
                </div>

                {/* Priority फक्त Follow-up status ला दाखवा */}
                {isFollow && (
                  <div>
                    <label style={lbl}>Priority <span style={{ color:"#DC2626" }}>*</span></label>
                    <SD
                      options={PRIORITY_OPTIONS}
                      value={s2.priority}
                      onChange={v => setS2({...s2, priority:v})}
                    />
                  </div>
                )}
              </div>

              {/* Follow-up Details — shown only when status includes "follow" */}
              {isFollow && (
                <>
                  <div style={divider} />
                  <SecTitle>Follow-up Details</SecTitle>
                  <div className="grid-2s" style={{ ...g(2), marginBottom:14 }}>
                    <div>
                      <label style={lbl}>Assign To <span style={{ color:"#DC2626" }}>*</span></label>
                      <EmpSD options={employees} value={s2.assignTo} onChange={v => setS2({...s2,assignTo:v})} loading={loadingMaster} />
                    </div>
                    <div>
                      <label style={lbl}>Comm. Mode <span style={{ color:"#DC2626" }}>*</span></label>
                      <CommSD options={commModes} value={s2.commMode} onChange={v => setS2({...s2,commMode:v})} loading={loadingMaster} />
                    </div>
                    <div>
                      <label style={lbl}>Call Lead Date</label>
                      <input style={inp} type="date" value={s2.callLeadDate} onChange={e => setS2({...s2,callLeadDate:e.target.value})} />
                    </div>
                    <div>
                      <label style={lbl}>Call Lead Time</label>
                      <input style={inp} type="time" value={s2.callLeadTime} onChange={e => setS2({...s2,callLeadTime:e.target.value})} />
                    </div>
                  </div>
                </>
              )}

              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:22, gap:8, flexWrap:"wrap" }}>
                <Btn variant="ghost" onClick={() => setStep(1)}>← Back</Btn>
                <Btn
                  variant={isSuccess ? "success" : isCancel ? "danger" : "primary"}
                  onClick={saveLead}
                  disabled={submitting}>
                  {submitting ? "Saving..." : isEdit ? "💾 Update Lead" : "💾 Save Lead"}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal     && <CustomerModal onSave={addCust} onClose={() => setShowModal(false)} />}
      {showAreaModal && <AreaModal     onSave={addArea} onClose={() => setShowAreaModal(false)} />}
    </div>
  );
};

export default LeadEnquiry;
